import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import SearchableSelect from '@/components/common/SearchableSelect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Search,
  UserPlus,
  Shield,
  Trash2,
  AlertCircle,
  Copy,
  Check,
  Loader2,
  Users,
  RefreshCw,
  Building2,
  Eye,
  Plus,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

interface User {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  enabled: boolean;
  is_super_admin: boolean;
  customer_org_count?: number;
}

interface UserDetails extends User {
  customer_orgs: Array<{
    id: string;
    name: string;
    path: string;
    role: string;
  }>;
  groups: Array<{
    id: string;
    name: string;
    path: string;
  }>;
}

interface OrgSubgroup {
  id: string;
  name: string;
  path: string;
}

interface CustomerOrg {
  id: string;
  name: string;
  path: string;
  subgroups: OrgSubgroup[];
}

interface AvailableOrgs {
  customer_orgs: CustomerOrg[];
}

interface UsersResponse {
  users: User[];
  count: number;
  total: number;
}

interface InviteResult {
  success: boolean;
  email_sent: boolean;
  temporary_password: string | null;
  user_id: string;
}

export function UserManagementView() {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteResult, setInviteResult] = useState<InviteResult | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [addOrgDialogOpen, setAddOrgDialogOpen] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [selectedSubgroupId, setSelectedSubgroupId] = useState<string>('none');
  const [addingToOrg, setAddingToOrg] = useState(false);
  const queryClient = useQueryClient();

  // Debounce search
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    const timer = setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Fetch users
  const { data, isLoading, error, refetch } = useQuery<UsersResponse>({
    queryKey: ['global-admin-users', debouncedSearch],
    queryFn: async () => {
      const client = await authenticatedBitswanBackendInstance();
      const response = await client.get('/global-admin/users/list/', {
        params: {
          search: debouncedSearch,
          limit: 100,
          offset: 0,
        },
      });
      return response.data;
    },
  });

  // Fetch user details
  const { data: userDetails, isLoading: isLoadingDetails } = useQuery<UserDetails>({
    queryKey: ['global-admin-user-details', selectedUserId],
    queryFn: async () => {
      const client = await authenticatedBitswanBackendInstance();
      const response = await client.get(`/global-admin/users/${selectedUserId}/details/`);
      return response.data;
    },
    enabled: !!selectedUserId && detailsDialogOpen,
  });

  // Fetch available orgs
  const { data: availableOrgs } = useQuery<AvailableOrgs>({
    queryKey: ['global-admin-available-orgs'],
    queryFn: async () => {
      const client = await authenticatedBitswanBackendInstance();
      const response = await client.get('/global-admin/users/available-orgs/');
      return response.data;
    },
    enabled: addOrgDialogOpen,
  });

  // Invite user mutation
  const inviteMutation = useMutation({
    mutationFn: async (email: string) => {
      const client = await authenticatedBitswanBackendInstance();
      const response = await client.post('/global-admin/users/invite/', { email });
      return response.data as InviteResult;
    },
    onSuccess: (result) => {
      setInviteResult(result);
      if (result.email_sent) {
        toast.success('Invitation email sent successfully');
      }
      queryClient.invalidateQueries({ queryKey: ['global-admin-users'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to invite user');
    },
  });

  // Toggle super admin mutation
  const toggleSuperAdminMutation = useMutation({
    mutationFn: async ({ userId, makeSuperAdmin }: { userId: string; makeSuperAdmin: boolean }) => {
      const client = await authenticatedBitswanBackendInstance();
      const response = await client.post('/global-admin/users/toggle-super-admin/', {
        user_id: userId,
        make_super_admin: makeSuperAdmin,
      });
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['global-admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['global-admin-user-details'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update super admin status');
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async ({ userId, confirmEmail }: { userId: string; confirmEmail: string }) => {
      const client = await authenticatedBitswanBackendInstance();
      const response = await client.post('/global-admin/users/delete/', {
        user_id: userId,
        confirm_email: confirmEmail,
      });
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      setDeleteConfirmEmail('');
      queryClient.invalidateQueries({ queryKey: ['global-admin-users'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete user');
    },
  });

  // Remove from group mutation
  const removeFromGroupMutation = useMutation({
    mutationFn: async ({ userId, groupId }: { userId: string; groupId: string }) => {
      const client = await authenticatedBitswanBackendInstance();
      const response = await client.post('/global-admin/users/remove-from-group/', {
        user_id: userId,
        group_id: groupId,
      });
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['global-admin-user-details'] });
      queryClient.invalidateQueries({ queryKey: ['global-admin-users'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to remove user from group');
    },
  });

  const handleInvite = () => {
    if (!inviteEmail.trim()) return;
    inviteMutation.mutate(inviteEmail.trim());
  };

  const handleCopyPassword = () => {
    if (inviteResult?.temporary_password) {
      navigator.clipboard.writeText(inviteResult.temporary_password);
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    }
  };

  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false);
    setInviteEmail('');
    setInviteResult(null);
  };

  const handleOpenDeleteDialog = (user: User) => {
    setUserToDelete(user);
    setDeleteConfirmEmail('');
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    if (!userToDelete) return;
    deleteUserMutation.mutate({
      userId: userToDelete.id,
      confirmEmail: deleteConfirmEmail,
    });
  };

  const handleOpenDetails = (userId: string) => {
    setSelectedUserId(userId);
    setDetailsDialogOpen(true);
  };

  const handleAddToOrg = async () => {
    if (!selectedUserId || !selectedOrgId) return;
    setAddingToOrg(true);
    try {
      const client = await authenticatedBitswanBackendInstance();

      // Add to the base org first
      await client.post('/global-admin/users/add-to-group/', {
        user_id: selectedUserId,
        group_id: selectedOrgId,
      });

      // If a specific role/subgroup was selected, add to that too
      if (selectedSubgroupId !== 'none') {
        await client.post('/global-admin/users/add-to-group/', {
          user_id: selectedUserId,
          group_id: selectedSubgroupId,
        });
      }

      toast.success('User added to organization');
      setAddOrgDialogOpen(false);
      setSelectedOrgId('');
      setSelectedSubgroupId('none');
      queryClient.invalidateQueries({ queryKey: ['global-admin-user-details'] });
      queryClient.invalidateQueries({ queryKey: ['global-admin-users'] });
    } catch {
      toast.error('Failed to add user to organization');
    } finally {
      setAddingToOrg(false);
    }
  };

  const handleRemoveFromGroup = (groupId: string) => {
    if (!selectedUserId) return;
    removeFromGroupMutation.mutate({ userId: selectedUserId, groupId });
  };

  const getSelectedOrg = () => {
    if (!availableOrgs || !selectedOrgId) return null;
    return availableOrgs.customer_orgs.find(o => o.id === selectedOrgId);
  };

  const users = data?.users || [];

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex gap-4">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>
          Failed to load users. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with search and actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative flex-1 w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search users by email or name..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <UserPlus className="h-4 w-4 mr-2" />
                Invite User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite New User</DialogTitle>
                <DialogDescription>
                  Send an invitation email to a new user. If the email server is not configured,
                  you'll receive temporary credentials to share manually.
                </DialogDescription>
              </DialogHeader>

              {!inviteResult ? (
                <>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="invite-email">Email Address</Label>
                      <Input
                        id="invite-email"
                        type="email"
                        placeholder="user@example.com"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={handleCloseInviteDialog}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleInvite}
                      disabled={!inviteEmail.trim() || inviteMutation.isPending}
                    >
                      {inviteMutation.isPending && (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      )}
                      Send Invitation
                    </Button>
                  </DialogFooter>
                </>
              ) : (
                <>
                  <div className="space-y-4 py-4">
                    {inviteResult.email_sent ? (
                      <Alert>
                        <Check className="h-4 w-4" />
                        <AlertTitle>Invitation Sent</AlertTitle>
                        <AlertDescription>
                          An invitation email has been sent to <strong>{inviteEmail}</strong>.
                          They will receive instructions to set their password.
                        </AlertDescription>
                      </Alert>
                    ) : (
                      <Alert variant="default" className="border-orange-200 bg-orange-50">
                        <AlertCircle className="h-4 w-4 text-orange-600" />
                        <AlertTitle className="text-orange-800">Email Not Sent</AlertTitle>
                        <AlertDescription className="text-orange-700">
                          The email server is not configured. Please share these temporary credentials with the user manually.
                        </AlertDescription>
                      </Alert>
                    )}

                    {inviteResult.temporary_password && (
                      <div className="space-y-2 p-4 rounded-lg bg-muted">
                        <Label className="text-sm font-medium">Temporary Credentials</Label>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Email:</span>
                            <code className="text-sm bg-background px-2 py-1 rounded">{inviteEmail}</code>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">Password:</span>
                            <div className="flex items-center gap-2">
                              <code className="text-sm bg-background px-2 py-1 rounded font-mono">
                                {inviteResult.temporary_password}
                              </code>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={handleCopyPassword}
                              >
                                {copiedPassword ? (
                                  <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          The user will be prompted to change this password on first login.
                        </p>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button onClick={handleCloseInviteDialog}>Done</Button>
                  </DialogFooter>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4" />
          <span>{data?.total || 0} total users</span>
        </div>
        {debouncedSearch && (
          <span>
            Showing {users.length} matching "{debouncedSearch}"
          </span>
        )}
      </div>

      {/* Users table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Organizations</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Super Admin</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  {debouncedSearch ? 'No users found matching your search' : 'No users found'}
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{user.email}</span>
                      {(user.firstName || user.lastName) && (
                        <span className="text-sm text-muted-foreground">
                          {[user.firstName, user.lastName].filter(Boolean).join(' ')}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {(user.customer_org_count ?? 0) > 0 ? (
                        <Badge variant="outline" className="gap-1">
                          <Building2 className="h-3 w-3" />
                          {user.customer_org_count}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">None</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={user.enabled ? 'default' : 'secondary'}>
                      {user.enabled ? 'Active' : 'Disabled'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={user.is_super_admin}
                        onCheckedChange={(checked) =>
                          toggleSuperAdminMutation.mutate({
                            userId: user.id,
                            makeSuperAdmin: checked,
                          })
                        }
                        disabled={toggleSuperAdminMutation.isPending}
                      />
                      {user.is_super_admin && (
                        <Shield className="h-4 w-4 text-primary" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDetails(user.id)}
                        title="View details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleOpenDeleteDialog(user)}
                        title="Delete user"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* User Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
            <DialogDescription>
              View and manage organization memberships for this user.
            </DialogDescription>
          </DialogHeader>

          {isLoadingDetails ? (
            <div className="space-y-4 py-4">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : userDetails ? (
            <ScrollArea className="max-h-[60vh] pr-4">
              <div className="space-y-6 py-4">
                {/* User info */}
                <div className="space-y-2">
                  <h4 className="font-medium">{userDetails.email}</h4>
                  {(userDetails.firstName || userDetails.lastName) && (
                    <p className="text-sm text-muted-foreground">
                      {[userDetails.firstName, userDetails.lastName].filter(Boolean).join(' ')}
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <Badge variant={userDetails.enabled ? 'default' : 'secondary'}>
                      {userDetails.enabled ? 'Active' : 'Disabled'}
                    </Badge>
                    {userDetails.is_super_admin && (
                      <Badge className="gap-1">
                        <Shield className="h-3 w-3" />
                        Super Admin
                      </Badge>
                    )}
                  </div>
                </div>

                <Separator />

                <Accordion type="single" collapsible defaultValue="customer-orgs">
                  <AccordionItem value="customer-orgs">
                    <AccordionTrigger>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Organizations ({userDetails.customer_orgs.length})
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2">
                        {userDetails.customer_orgs.length === 0 ? (
                          <p className="text-sm text-muted-foreground py-2">
                            Not a member of any organization.
                          </p>
                        ) : (
                          userDetails.customer_orgs.map((org) => (
                            <div
                              key={org.id}
                              className="flex items-center justify-between p-2 rounded-lg border"
                            >
                              <div>
                                <span className="font-medium">{org.name}</span>
                                <Badge variant="outline" className="ml-2 text-xs">
                                  {org.role}
                                </Badge>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                onClick={() => handleRemoveFromGroup(org.id)}
                                disabled={removeFromGroupMutation.isPending}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="all-groups">
                    <AccordionTrigger>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        All Groups ({userDetails.groups.length})
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-1">
                        {userDetails.groups.map((group) => (
                          <div
                            key={group.id}
                            className="flex items-center justify-between py-1 px-2 text-sm"
                          >
                            <code className="text-xs">{group.path}</code>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                              onClick={() => handleRemoveFromGroup(group.id)}
                              disabled={removeFromGroupMutation.isPending}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </ScrollArea>
          ) : null}

          <DialogFooter className="flex-row justify-between sm:justify-between">
            <Button
              variant="outline"
              onClick={() => {
                setAddOrgDialogOpen(true);
                setSelectedOrgId('');
                setSelectedSubgroupId('none');
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add to Organization
            </Button>
            <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to Organization Dialog */}
      <Dialog open={addOrgDialogOpen} onOpenChange={setAddOrgDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add User to Organization</DialogTitle>
            <DialogDescription>
              Select an organization and optionally a role to add this user to.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Organization</Label>
              <SearchableSelect
                value={selectedOrgId}
                onChange={(value) => {
                  setSelectedOrgId(value);
                  setSelectedSubgroupId('none');
                }}
                placeholder="Select organization..."
                searchPlaceholder="Search organizations…"
                options={(availableOrgs?.customer_orgs ?? []).map((org) => ({ value: org.id, label: org.name }))}
                testId="add-org-select"
              />
            </div>

            {selectedOrgId && (
              <div className="space-y-2">
                <Label>Role / Subgroup (optional)</Label>
                <SearchableSelect
                  value={selectedSubgroupId}
                  onChange={setSelectedSubgroupId}
                  placeholder="Select role..."
                  searchPlaceholder="Search roles…"
                  options={[
                    { value: 'none', label: 'Member only (no specific role)' },
                    ...(getSelectedOrg()?.subgroups ?? []).map((sg) => ({ value: sg.id, label: sg.name })),
                  ]}
                  testId="add-org-role-select"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOrgDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddToOrg}
              disabled={!selectedOrgId || addingToOrg}
            >
              {addingToOrg && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Add to Organization
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete User</DialogTitle>
            <DialogDescription>
              This action cannot be undone. The user will be permanently removed from the system.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Warning</AlertTitle>
              <AlertDescription>
                You are about to delete <strong>{userToDelete?.email}</strong>.
                This will remove all their access and data.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="confirm-email">
                Type the user's email to confirm deletion:
              </Label>
              <Input
                id="confirm-email"
                type="email"
                placeholder={userToDelete?.email}
                value={deleteConfirmEmail}
                onChange={(e) => setDeleteConfirmEmail(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={
                deleteConfirmEmail.toLowerCase() !== userToDelete?.email?.toLowerCase() ||
                deleteUserMutation.isPending
              }
            >
              {deleteUserMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
